/**
 * Central registry of data-testid values used for UI automation (Playwright).
 *
 * This file is the single source of truth for locators — copy it into the
 * automation repo and reference these constants from page objects instead of
 * hard-coding strings.
 *
 * Conventions:
 * - Every id is prefixed `cuf-` (this app is embedded in OTCIQ as a
 *   micro-frontend; the prefix avoids collisions with parent-shell locators).
 * - Kebab-case, hierarchical: `cuf-<area>-<element>`.
 * - Row-level ids are data-derived (never index-derived — tables are sorted),
 *   exposed as helper functions, e.g. `incorpHistory.deleteBtn(item.id)`.
 *
 * Attachment pattern (verified against otcm-ui-lib v4.16.10 source):
 * - Buttons, checkboxes, dialogs, tables and containers carry the id on the
 *   element itself (the lib spreads rest props to the DOM).
 * - Form FIELD ids live on the app-owned wrapper <Box> around the field, NOT
 *   on the lib field component: TextField/SelectField/DateField drop unknown
 *   props in their read-only render branch, so a wrapper id is the only
 *   locator that exists in both edit and read-only modes.
 *
 * Playwright usage:
 *   // field, edit mode — scope a role query inside the wrapper:
 *   page.getByTestId(testIds.companyInfo.general.businessDesc).getByRole('textbox')
 *   // field, read-only mode — the wrapper's text content is the value:
 *   page.getByTestId(testIds.companyInfo.filing.filingCycle)
 *   // buttons/dialogs — direct:
 *   page.getByTestId(testIds.companyInfo.incorpHistory.addBtn)
 *   // scoped row lookup:
 *   page.getByTestId(testIds.companyInfo.incorpHistory.table)
 *       .getByRole('row', { hasText: 'Delaware' })
 */
export const testIds = {
  nav: 'cuf-nav',
  controlPanel: {
    root: 'cuf-control-panel',
    startBtn: 'cuf-start-btn',
    startDialog: 'cuf-start-dialog',
    saveBtn: 'cuf-save-btn',
    reopenBtn: 'cuf-reopen-btn',
    reopenDialog: 'cuf-reopen-dialog',
    reopenBackBtn: 'cuf-reopen-back-btn',
    reopenConfirmBtn: 'cuf-reopen-confirm-btn',
    discardBtn: 'cuf-discard-btn',
    discardDialog: 'cuf-discard-dialog',
    discardBackBtn: 'cuf-discard-back-btn',
    discardConfirmBtn: 'cuf-discard-confirm-btn',
    verificationHistoryItem: 'cuf-verification-history-item',
    verificationHistoryDialog: 'cuf-verification-history-dialog',
    verificationHistoryTable: 'cuf-verification-history-table'
  },
  support: {
    instructionsBtn: 'cuf-instructions-btn',
    instructionsDialog: 'cuf-instructions-dialog',
    helpBtn: 'cuf-help-btn',
    helpDialog: 'cuf-help-dialog'
  },
  submitProgressDialog: 'cuf-submit-progress-dialog',
  companyInfo: {
    section: 'cuf-company-info-section',
    general: {
      businessDesc: 'cuf-company-info-business-desc',
      facilitiesDesc: 'cuf-company-info-facilities-desc',
      productServiceDesc: 'cuf-company-info-product-service-desc',
      numEmployees: 'cuf-company-info-num-employees',
      numEmployeesAsOf: 'cuf-company-info-num-employees-as-of'
    },
    incorpHistory: {
      addBtn: 'cuf-incorp-history-add-btn',
      table: 'cuf-incorp-history-table',
      deleteBtn: id => `cuf-incorp-history-delete-${id}`,
      dialog: 'cuf-incorp-history-dialog',
      country: 'cuf-incorp-history-country',
      state: 'cuf-incorp-history-state',
      year: 'cuf-incorp-history-year',
      dialogCancelBtn: 'cuf-incorp-history-cancel-btn',
      dialogSubmitBtn: 'cuf-incorp-history-submit-btn',
      removeDialog: 'cuf-incorp-history-remove-dialog',
      removeCancelBtn: 'cuf-incorp-history-remove-cancel-btn',
      removeConfirmBtn: 'cuf-incorp-history-remove-confirm-btn'
    },
    nameHistory: {
      addBtn: 'cuf-name-history-add-btn',
      table: 'cuf-name-history-table',
      deleteBtn: id => `cuf-name-history-delete-${id}`,
      dialog: 'cuf-name-history-dialog',
      previousName: 'cuf-name-history-previous-name',
      newName: 'cuf-name-history-new-name',
      effectiveDate: 'cuf-name-history-effective-date',
      dialogCancelBtn: 'cuf-name-history-cancel-btn',
      dialogSubmitBtn: 'cuf-name-history-submit-btn',
      removeDialog: 'cuf-name-history-remove-dialog',
      removeCancelBtn: 'cuf-name-history-remove-cancel-btn',
      removeConfirmBtn: 'cuf-name-history-remove-confirm-btn'
    },
    filing: {
      fyEndMonth: 'cuf-filing-fy-end-month',
      fyEndDay: 'cuf-filing-fy-end-day',
      filingCycle: 'cuf-filing-cycle',
      primarySicCode: 'cuf-filing-primary-sic-code',
      cik: 'cuf-filing-cik'
    },
    orgStage: {
      blankCheck: 'cuf-org-stage-blank-check',
      shell: 'cuf-org-stage-shell',
      spac: 'cuf-org-stage-spac',
      blindPool: 'cuf-org-stage-blind-pool'
    },
    // Verify box container is the click target; its checkbox renders with the
    // derived id `${verifyBox}-checkbox` for state assertions.
    verifyBox: 'cuf-company-info-verify-box',
    nextBtn: 'cuf-company-info-next-btn'
  }
};
